import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ExternalLink, Settings, ArrowUp, ArrowDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase, UsefulLink, usefulLinksHelpers } from '../lib/supabase';
import UsefulLinkCard from './UsefulLinkCard';
import UsefulLinkModal from './UsefulLinkModal';
import ConfirmationModal from './ConfirmationModal';
import { loadPartnerTools, updatePartnerTool, createPartnerTool, PartnerTool } from '../helpers/toolsHelpers';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ImageUploader from './ImageUploader';

const defaultLogos: Record<string, string> = {
  mastershop: '/logo.png',
  precify: '/logo.png',
  utmfy: '/logo.png',
};

const UsefulLinksPage: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const { showToast } = useToast();
  
  // Estados para Links Úteis
  const [links, setLinks] = useState<UsefulLink[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<UsefulLink | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<UsefulLink | null>(null);
  const [loadingLinks, setLoadingLinks] = useState(true);

  // Estados para Ferramentas Parceiras
  const [activeTab, setActiveTab] = useState<'links' | 'tools'>('links');
  const [tools, setTools] = useState<PartnerTool[]>([]);
  const [loadingTools, setLoadingTools] = useState(true);
  const [editingToolId, setEditingToolId] = useState<string | null>(null);
  const [isAddingTool, setIsAddingTool] = useState(false);
  const [toolForm, setToolForm] = useState<{ name: string; href: string; logo_url: string; status: 'acessar' | 'em breve'; button_color: string }>({ name: '', href: '', logo_url: '', status: 'acessar', button_color: '#0FFF55' });

  // Carregar links do Supabase
  const loadLinks = async () => {
    try {
      console.log('🔄 Iniciando carregamento de links...');
      setLoadingLinks(true);
      
      const { data, error } = await usefulLinksHelpers.getAll();
      
      if (error) {
        console.error('❌ Erro ao carregar links:', error);
        return;
      }
      
      console.log('✅ Links carregados:', data?.length || 0, 'links');
      setLinks((data || []).map((l: any) => ({ ...l, image: (l as any).image_with_bust || l.image })));
    } catch (error) {
      console.error('❌ Erro inesperado ao carregar links:', error);
    } finally {
      setLoadingLinks(false);
    }
  };

  // Carregar ferramentas parceiras
  const loadTools = async () => {
    try {
      setLoadingTools(true);
      const list = await loadPartnerTools();
      setTools(list);
    } catch (error) {
      console.error('Erro ao carregar ferramentas parceiras:', error);
    } finally {
      setLoadingTools(false);
    }
  };

  // Carregar dados na inicialização
  useEffect(() => {
    const initializePage = async () => {
      // Debug authentication first
      const authDebug = await usefulLinksHelpers.debugAuth();
      console.log('🔍 Initial auth debug:', authDebug);
      
      loadLinks();
      loadTools();
    };
    
    initializePage();
  }, []);

  // Inscrever para mudanças em tempo real nos links úteis
  useEffect(() => {
    console.log('🔄 Configurando subscription para useful_links');
    
    const subscription = usefulLinksHelpers.subscribeToChanges((payload: any) => {
      console.log('📡 Mudança detectada nos links úteis:', payload);
      
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE' || payload.eventType === 'DELETE') {
        console.log('🔄 Recarregando links após mudança:', payload.eventType);
        loadLinks();
      }
    });

    return () => {
      console.log('🛑 Desconectando subscription de useful_links');
      subscription.unsubscribe();
    };
  }, []);

  const handleCreateLink = () => {
    setEditingLink(null);
    setIsModalOpen(true);
  };

  const handleEditLink = (link: UsefulLink) => {
    setEditingLink(link);
    setIsModalOpen(true);
  };

  const handleDeleteLink = (link: UsefulLink) => {
    setLinkToDelete(link);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (linkToDelete) {
      try {
        if (!isAdmin) {
          showToast({
            type: 'error',
            title: 'Permissão Negada',
            message: 'Você não tem permissão para deletar links.'
          });
          return;
        }

        const { success, error } = await usefulLinksHelpers.delete(linkToDelete.id);
        
        if (error) {
          showToast({
            type: 'error',
            title: 'Erro ao Deletar',
            message: `Erro ao deletar link: ${error.message || 'Erro desconhecido'}`
          });
          return;
        }
        
        if (success) {
          setLinkToDelete(null);
          setIsDeleteModalOpen(false);
          await loadLinks();
          
          showToast({
            type: 'success',
            title: 'Link Deletado!',
            message: 'O link foi removido com sucesso.'
          });
        }
      } catch (error) {
        showToast({
          type: 'error',
          title: 'Erro Inesperado',
          message: `Erro inesperado ao deletar link.`
        });
      }
    }
  };

  const handleSaveLink = async (linkData: Omit<UsefulLink, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'is_active'>) => {
    try {
      if (editingLink) {
        const { error } = await usefulLinksHelpers.update(editingLink.id, linkData);
        if (error) {
          showToast({
            type: 'error',
            title: 'Erro ao Atualizar',
            message: 'Não foi possível atualizar o link.'
          });
          return;
        }
        
        showToast({
          type: 'success',
          title: 'Link Atualizado!',
          message: 'O link foi salvo com sucesso.'
        });
      } else {
        const { error } = await usefulLinksHelpers.create({
          ...linkData,
          created_by: user?.id || '',
          is_active: true,
          order_index: links.length
        });
        
        if (error) {
          showToast({
            type: 'error',
            title: 'Erro ao Criar',
            message: 'Não foi possível criar o link.'
          });
          return;
        }
        
        showToast({
          type: 'success',
          title: 'Link Criado!',
          message: 'O link foi adicionado com sucesso.'
        });
      }
      
      setIsModalOpen(false);
      setEditingLink(null);
      await loadLinks();
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Erro Inesperado',
        message: 'Ocorreu um erro inesperado ao salvar o link.'
      });
    }
  };

  const startEditTool = (tool: PartnerTool) => {
    setEditingToolId(tool.id);
    setToolForm({
      name: tool.name || '',
      href: tool.href || '',
      logo_url: tool.logo_url || '',
      status: (tool as any).status || 'acessar',
      button_color: (tool as any).button_color || '#0FFF55'
    });
  };

  const normalizeGitHubUrl = (url: string) => {
    try {
      let u = url.trim();
      u = u.replace(/\?.*$/, '');
      if (u.includes('github.com')) {
        u = u
          .replace('https://github.com/', 'https://raw.githubusercontent.com/')
          .replace('/blob/', '/');
      }
      u = u.replace('/refs/heads/', '/');
      return u;
    } catch {
      return url;
    }
  };

  const saveToolEdit = async (tool: PartnerTool) => {
    const normalizedLogo = normalizeGitHubUrl(toolForm.logo_url.trim());
    const updated = await updatePartnerTool(tool.id, {
      name: toolForm.name.trim(),
      href: toolForm.href.trim(),
      logo_url: normalizedLogo,
      status: toolForm.status,
      button_color: toolForm.button_color
    } as any);
    if (updated) {
      setTools(prev => prev.map(t => (t.id === tool.id ? updated : t)));
      setEditingToolId(null);
      showToast({
        type: 'success',
        title: 'Ferramenta atualizada',
        message: 'A ferramenta parceira foi salva com sucesso!'
      });
    } else {
      showToast({
        type: 'error',
        title: 'Erro ao salvar',
        message: 'Não foi possível atualizar a ferramenta parceira.'
      });
    }
  };

  const handleCreateTool = () => {
    setIsAddingTool(true);
    setEditingToolId(null);
    setToolForm({
      name: '',
      href: '',
      logo_url: '',
      status: 'acessar',
      button_color: '#0FFF55'
    });
  };

  const saveNewTool = async () => {
    if (!toolForm.name.trim()) {
      showToast({
        type: 'error',
        title: 'Nome obrigatório',
        message: 'Por favor, insira o nome da ferramenta.'
      });
      return;
    }

    const normalizedLogo = normalizeGitHubUrl(toolForm.logo_url.trim());
    const newTool = await createPartnerTool({
      name: toolForm.name.trim(),
      href: toolForm.href.trim(),
      logo_url: normalizedLogo,
      status: toolForm.status,
      button_color: toolForm.button_color,
      category: null,
      order_index: tools.length,
      is_active: true
    } as any);

    if (newTool) {
      setTools(prev => [...prev, newTool]);
      setIsAddingTool(false);
      showToast({
        type: 'success',
        title: 'Ferramenta adicionada',
        message: 'A nova ferramenta parceira foi criada com sucesso!'
      });
    } else {
      showToast({
        type: 'error',
        title: 'Erro ao criar',
        message: 'Não foi possível adicionar a ferramenta parceira.'
      });
    }
  };

  const handleDeleteTool = async (tool: PartnerTool) => {
    const updated = await updatePartnerTool(tool.id, { is_active: false });
    if (updated) {
      setTools(prev => prev.filter(t => t.id !== tool.id));
      showToast({
        type: 'success',
        title: 'Ferramenta excluída',
        message: 'A ferramenta parceira foi excluída com sucesso!'
      });
    } else {
      showToast({
        type: 'error',
        title: 'Erro ao excluir',
        message: 'Não foi possível excluir a ferramenta parceira.'
      });
    }
  };

  const handleMoveLink = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= links.length) return;

    const currentLink = links[index];
    const targetLink = links[targetIndex];

    let currentIdxValue = currentLink.order_index;
    let targetIdxValue = targetLink.order_index;

    if (currentIdxValue === targetIdxValue) {
      currentIdxValue = index;
      targetIdxValue = targetIndex;
    }

    const temp = currentIdxValue;
    currentIdxValue = targetIdxValue;
    targetIdxValue = temp;

    try {
      const updatePromises = [
        usefulLinksHelpers.update(currentLink.id, { order_index: currentIdxValue }),
        usefulLinksHelpers.update(targetLink.id, { order_index: targetIdxValue })
      ];
      await Promise.all(updatePromises);
      showToast({
        type: 'success',
        title: 'Ordem Atualizada',
        message: 'A ordem dos links foi atualizada com sucesso.'
      });
      await loadLinks();
    } catch (error) {
      console.error('Erro ao reordenar links:', error);
      showToast({
        type: 'error',
        title: 'Erro ao Reordenar',
        message: 'Não foi possível salvar a nova ordem.'
      });
    }
  };

  const handleMoveTool = async (index: number, direction: 'up' | 'down') => {
    const uniqueTools = tools.filter((t, idx, arr) => arr.findIndex(x => x.id === t.id) === idx);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= uniqueTools.length) return;

    const currentTool = uniqueTools[index];
    const targetTool = uniqueTools[targetIndex];

    let currentIdxValue = currentTool.order_index;
    let targetIdxValue = targetTool.order_index;

    if (currentIdxValue === targetIdxValue) {
      currentIdxValue = index;
      targetIdxValue = targetIndex;
    }

    const temp = currentIdxValue;
    currentIdxValue = targetIdxValue;
    targetIdxValue = temp;

    try {
      const updatePromises = [
        updatePartnerTool(currentTool.id, { order_index: currentIdxValue }),
        updatePartnerTool(targetTool.id, { order_index: targetIdxValue })
      ];
      await Promise.all(updatePromises);
      showToast({
        type: 'success',
        title: 'Ordem Atualizada',
        message: 'A ordem das ferramentas foi atualizada com sucesso.'
      });
      await loadTools();
    } catch (error) {
      console.error('Erro ao reordenar ferramentas:', error);
      showToast({
        type: 'error',
        title: 'Erro ao Reordenar',
        message: 'Não foi possível salvar a nova ordem.'
      });
    }
  };

  if (loadingLinks && links.length === 0) {
    return (
      <div className="flex-1 bg-[#121316] min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#0FFF55]"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-[#121316] min-h-screen pt-20">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-[#1a1d21] via-[#121316] to-[#0f1114] border-b border-gray-800 md:-ml-[70px] xl:-ml-[80px] w-full md:w-[calc(100%+70px)] xl:w-[calc(100%+80px)]">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%230FFF55%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50" />
        
        <div className="relative p-8 md:pl-[102px] xl:pl-[112px]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-4xl font-bold text-white mb-4">
                  Ferramentas e <span className="text-[#0FFF55]">Links Úteis</span>
                </h1>
                <p className="text-gray-300 text-lg max-w-3xl leading-relaxed">
                  Uma coleção de recursos, ferramentas parceiras e links úteis para acelerar seus resultados.
                </p>
              </div>
              
              {isAdmin && activeTab === 'links' && (
                <button
                  onClick={handleCreateLink}
                  className="flex items-center space-x-2 bg-[#0FFF55] text-[#121316] px-6 py-3 rounded-2xl hover:bg-[#0dd149] transition-colors font-semibold"
                >
                  <Plus size={20} />
                  <span>Adicionar Link</span>
                </button>
              )}
              {isAdmin && activeTab === 'tools' && (
                <button
                  onClick={handleCreateTool}
                  className="flex items-center space-x-2 bg-[#0FFF55] text-[#121316] px-6 py-3 rounded-2xl hover:bg-[#0dd149] transition-colors font-semibold"
                >
                  <Plus size={20} />
                  <span>Adicionar Ferramenta</span>
                </button>
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center space-x-8">
              {activeTab === 'links' ? (
                <div className="flex items-center space-x-2">
                  <ExternalLink className="w-5 h-5 text-[#0FFF55]" />
                  <span className="text-white font-semibold">{links.length}</span>
                  <span className="text-gray-400 text-sm">links disponíveis</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Settings className="w-5 h-5 text-[#0FFF55]" />
                  <span className="text-white font-semibold">{tools.length}</span>
                  <span className="text-gray-400 text-sm">ferramentas parceiras</span>
                </div>
              )}
              {isAdmin && (
                <div className="flex items-center space-x-2">
                  <Settings className="w-5 h-5 text-[#0FFF55]" />
                  <span className="text-white font-semibold">Modo Admin</span>
                  <span className="text-gray-400 text-sm">gerenciamento ativo</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-800 mb-8 max-w-7xl mx-auto px-8 mt-6">
        <button
          onClick={() => setActiveTab('links')}
          className={`px-6 py-3 font-semibold text-base sm:text-lg border-b-2 transition-all duration-200 ${
            activeTab === 'links'
              ? 'border-[#0FFF55] text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Links Úteis
        </button>
        <button
          onClick={() => setActiveTab('tools')}
          className={`px-6 py-3 font-semibold text-base sm:text-lg border-b-2 transition-all duration-200 ${
            activeTab === 'tools'
              ? 'border-[#0FFF55] text-white'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Ferramentas Parceiras
        </button>
      </div>

      {/* Content */}
      <div className="p-8 pb-24 max-w-7xl mx-auto">
        {activeTab === 'links' ? (
          links.length === 0 ? (
            <div className="text-center py-16">
              <ExternalLink className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Nenhum link disponível</h3>
              <p className="text-gray-400 mb-6">
                {isAdmin 
                  ? "Adicione o primeiro link útil para começar a compartilhar recursos com a comunidade."
                  : "Aguarde enquanto nossos administradores adicionam links úteis para você."
                }
              </p>
              {isAdmin && (
                <button
                  onClick={handleCreateLink}
                  className="flex items-center space-x-2 bg-[#0FFF55] text-[#121316] px-6 py-3 rounded-2xl hover:bg-[#0dd149] transition-colors font-semibold mx-auto"
                >
                  <Plus size={20} />
                  <span>Adicionar Primeiro Link</span>
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {links.map((link, index) => (
                <UsefulLinkCard
                  key={link.id}
                  link={link}
                  isAdmin={isAdmin}
                  onEdit={() => handleEditLink(link)}
                  onDelete={() => handleDeleteLink(link)}
                  onMoveUp={index > 0 ? () => handleMoveLink(index, 'up') : undefined}
                  onMoveDown={index < links.length - 1 ? () => handleMoveLink(index, 'down') : undefined}
                />
              ))}
            </div>
          )
        ) : (
          loadingTools ? (
            <div className="text-center text-gray-400 py-16">Carregando ferramentas parceiras...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {isAdmin && isAddingTool && (
                <Card className="bg-[#1a1d23] border-[#0FFF55]/50 border-dashed border-2 transition-all duration-300">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-center">Nova Ferramenta</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col items-center text-center space-y-3">
                      <div className="w-full space-y-3 text-left">
                        <div>
                          <label className="text-xs text-gray-400 font-semibold">Nome da Ferramenta</label>
                          <Input
                            placeholder="Nome da ferramenta"
                            value={toolForm.name}
                            onChange={(e) => setToolForm({ ...toolForm, name: e.target.value })}
                            className="bg-[#0f1114] border-[#1f2329] text-white mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 font-semibold">URL de acesso</label>
                          <Input
                            placeholder="URL de acesso"
                            value={toolForm.href}
                            onChange={(e) => setToolForm({ ...toolForm, href: e.target.value })}
                            className="bg-[#0f1114] border-[#1f2329] text-white mt-1"
                          />
                        </div>
                        <div>
                          <ImageUploader
                            bucketName="ferramentas"
                            label="Logo da Ferramenta"
                            value={toolForm.logo_url}
                            onChange={(url) => setToolForm({ ...toolForm, logo_url: url })}
                            options={{ maxWidth: 200, maxHeight: 200, quality: 0.85, format: 'webp' }}
                            placeholder="Arraste imagem da logo ou cole a URL"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 font-semibold block mb-1">Status</label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setToolForm({ ...toolForm, status: 'acessar' })}
                              className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                toolForm.status === 'acessar' 
                                  ? 'bg-[#0FFF55] text-black border-[#0FFF55]' 
                                  : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-600'
                              }`}
                            >
                              Acessar
                            </button>
                            <button
                              type="button"
                              onClick={() => setToolForm({ ...toolForm, status: 'em breve' })}
                              className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                toolForm.status === 'em breve' 
                                  ? 'bg-amber-500 text-black border-amber-500' 
                                  : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-600'
                              }`}
                            >
                              Em Breve
                            </button>
                          </div>
                        </div>
                        {toolForm.status === 'em breve' && (
                          <div>
                            <label className="text-xs text-gray-400 font-semibold block mb-1">Cor Personalizada do Botão</label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={toolForm.button_color}
                                onChange={(e) => setToolForm({ ...toolForm, button_color: e.target.value })}
                                className="w-10 h-10 border border-gray-700 bg-transparent rounded cursor-pointer p-0"
                              />
                              <Input
                                placeholder="#HEXCODE"
                                value={toolForm.button_color}
                                onChange={(e) => setToolForm({ ...toolForm, button_color: e.target.value })}
                                className="bg-[#0f1114] border-[#1f2329] text-white flex-1"
                              />
                            </div>
                          </div>
                        )}
                        <div className="flex gap-2 pt-2">
                          <Button className="flex-1 bg-[#0FFF55] text-black hover:bg-[#0dd149] font-bold" onClick={saveNewTool}>Salvar</Button>
                          <Button variant="outline" className="flex-1 text-gray-400 hover:text-white" onClick={() => setIsAddingTool(false)}>Cancelar</Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {(() => {
                const uniqueTools = tools.filter((t, idx, arr) => arr.findIndex(x => x.id === t.id) === idx);
                return uniqueTools.map((tool, index) => (
                  <Card key={tool.id} className="bg-[#1a1d23] border-[#1f2329] hover:border-[#0FFF55]/30 transition-all duration-300">
                    <CardHeader className="pb-3">
                      <div className="w-full flex items-center justify-center">
                        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#0f1114] border border-[#1f2329] flex items-center justify-center">
                          {(() => {
                            const base = tool.logo_url || defaultLogos[tool.name.toLowerCase()] || '/logo.png';
                            const ts = new Date((tool as any).updated_at || '').getTime() || Date.now();
                            const src = base.includes('?') ? `${base}&t=${ts}` : `${base}?t=${ts}`;
                            return (
                              <img
                                src={src}
                                alt={tool.name}
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  const img = e.currentTarget as HTMLImageElement;
                                  if (img.dataset.fallbackApplied !== 'true') {
                                    img.dataset.fallbackApplied = 'true';
                                    img.src = (defaultLogos[tool.name.toLowerCase()] || '/logo.png') + `?t=${Date.now()}`;
                                  }
                                }}
                              />
                            );
                          })()}
                        </div>
                      </div>
                      <CardTitle className="text-white mt-3 text-center">{tool.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col items-center text-center">
                        {editingToolId === tool.id && isAdmin ? (
                          <div className="w-full space-y-3 mb-4 text-left">
                            <div>
                              <label className="text-xs text-gray-400 font-semibold">Nome da Ferramenta</label>
                              <Input
                                placeholder="Nome da ferramenta"
                                value={toolForm.name}
                                onChange={(e) => setToolForm({ ...toolForm, name: e.target.value })}
                                className="bg-[#0f1114] border-[#1f2329] text-white mt-1"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-400 font-semibold">URL de acesso</label>
                              <Input
                                placeholder="URL de acesso"
                                value={toolForm.href}
                                onChange={(e) => setToolForm({ ...toolForm, href: e.target.value })}
                                className="bg-[#0f1114] border-[#1f2329] text-white mt-1"
                              />
                            </div>
                            <div>
                              <ImageUploader
                                bucketName="ferramentas"
                                label="Logo da Ferramenta"
                                value={toolForm.logo_url}
                                onChange={(url) => setToolForm({ ...toolForm, logo_url: url })}
                                options={{ maxWidth: 200, maxHeight: 200, quality: 0.85, format: 'webp' }}
                                placeholder="Arraste imagem da logo ou cole a URL"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-400 font-semibold block mb-1">Status</label>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setToolForm({ ...toolForm, status: 'acessar' })}
                                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                    toolForm.status === 'acessar' 
                                      ? 'bg-[#0FFF55] text-black border-[#0FFF55]' 
                                      : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-600'
                                  }`}
                                >
                                  Acessar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setToolForm({ ...toolForm, status: 'em breve' })}
                                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                    toolForm.status === 'em breve' 
                                      ? 'bg-amber-500 text-black border-amber-500' 
                                      : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-600'
                                  }`}
                                >
                                  Em Breve
                                </button>
                              </div>
                            </div>
                            {toolForm.status === 'em breve' && (
                              <div>
                                <label className="text-xs text-gray-400 font-semibold block mb-1">Cor Personalizada do Botão</label>
                                <div className="flex gap-2">
                                  <input
                                    type="color"
                                    value={toolForm.button_color}
                                    onChange={(e) => setToolForm({ ...toolForm, button_color: e.target.value })}
                                    className="w-10 h-10 border border-gray-700 bg-transparent rounded cursor-pointer p-0"
                                  />
                                  <Input
                                    placeholder="#HEXCODE"
                                    value={toolForm.button_color}
                                    onChange={(e) => setToolForm({ ...toolForm, button_color: e.target.value })}
                                    className="bg-[#0f1114] border-[#1f2329] text-white flex-1"
                                  />
                                </div>
                              </div>
                            )}
                            <div className="flex gap-2 pt-2">
                              <Button className="flex-1 bg-[#0FFF55] text-black hover:bg-[#0dd149] font-bold" onClick={() => saveToolEdit(tool)}>Salvar</Button>
                              <Button variant="outline" className="flex-1 text-gray-400 hover:text-white" onClick={() => setEditingToolId(null)}>Cancelar</Button>
                            </div>
                          </div>
                        ) : (
                          (tool as any).status === 'em breve' ? (
                            <div className="w-full">
                              <div 
                                className="w-full text-center text-white font-semibold rounded-md py-2 select-none"
                                style={{ backgroundColor: (tool as any).button_color || '#F59E0B' }}
                              >
                                Em breve...
                              </div>
                            </div>
                          ) : (
                            <Button asChild className="w-full bg-[#0FFF55] text-black font-semibold hover:bg-[#0dd149]">
                              <a href={tool.href || '#'} target="_blank" rel="noreferrer">
                                Acessar
                              </a>
                            </Button>
                          )
                        )}

                        {isAdmin && editingToolId !== tool.id && (
                          <div className="flex gap-2 w-full mt-3">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleMoveTool(index, 'up')}
                              disabled={index === 0}
                              title="Mover para cima"
                              className="text-gray-400 border-gray-800 hover:text-white"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleMoveTool(index, 'down')}
                              disabled={index === uniqueTools.length - 1}
                              title="Mover para baixo"
                              className="text-gray-400 border-gray-800 hover:text-white"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              className="flex-1"
                              onClick={() => startEditTool(tool)}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              className="border-red-600 text-red-500 hover:text-red-400 hover:bg-red-500/10 flex-1"
                              onClick={() => handleDeleteTool(tool)}
                            >
                              Excluir
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ));
              })()}
            </div>
          )
        )}
      </div>

      {/* Modal para criar/editar link */}
      <UsefulLinkModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingLink(null);
        }}
        onSave={handleSaveLink}
        editingLink={editingLink}
      />

      {/* Modal de confirmação para deletar */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setLinkToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Excluir Link"
        message={`Tem certeza que deseja excluir o link "${linkToDelete?.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default UsefulLinksPage;